import base64js from 'base64-js';

const cloudfrontHostname = 'https://d2obrjcl8a4u8e.cloudfront.net';
export const s3Hostname = 'ntmg-media.s3.us-west-1.amazonaws.com';
const s3BucketName = 'ntmg-media';

const disableImageResizing = false;

// Current resizing strategy is to resize to ~2x of the maximum width/height that the
// image will be displayed at for crispness on retina screens.

type ImageEditRequest = {
  resize: {
    height?: number;
    width?: number;
    fit: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
  };
};

export const getFalconUIImageUrl = ({
  src,
  width,
  height,
}: {
  src: string;
  width?: number;
  height?: number;
}): string => {
  if (!width && !height) {
    return src;
  }

  return getResizedUrl(src, {
    resize: {
      width,
      height,
      fit: 'cover',
    },
  });
};

export const getImageUrl = (originalImageUrl: string, width: number, height?: number): string =>
  getResizedUrl(originalImageUrl, {
    resize: {
      width,
      height,
      fit: 'cover',
    },
  });

export const getResizedUrl = (
  originalImageUrl: string,
  imageEditRequest: ImageEditRequest
): string => {
  if (!originalImageUrl) {
    return '';
  }

  if (disableImageResizing) {
    return originalImageUrl;
  }

  if (!originalImageUrl.includes(s3Hostname)) {
    return originalImageUrl;
  }

  const parts = originalImageUrl.split(s3Hostname + '/');
  if (parts.length !== 2) {
    return originalImageUrl;
  }

  const key = parts[1]
    .split('/')
    .map((pathPart) => decodeURIComponent(pathPart))
    .join('/');
  const imageRequest = {
    bucket: s3BucketName,
    key,
    edits: imageEditRequest,
    v: 1,
  };

  const isServer = typeof window === 'undefined';
  if (isServer) {
    const buf = Buffer.from(JSON.stringify(imageRequest));
    return `${cloudfrontHostname}/${buf.toString('base64')}`;
  }

  return `${cloudfrontHostname}/${base64js.fromByteArray(
    Uint8Array.from(new TextEncoder().encode(JSON.stringify(imageRequest)))
  )}`;
};

export const getEditorJsResizedUrl = (originalImageUrl: string, imageEditRequest: any) => {
  if (!originalImageUrl) {
    return '';
  }

  if (!originalImageUrl.includes(s3Hostname)) {
    return originalImageUrl;
  }

  const parts = originalImageUrl.split(s3Hostname + '/');

  if (parts.length !== 2) {
    return originalImageUrl;
  }

  const key = parts[1]
    .split('/')
    .map((pathPart) => decodeURIComponent(pathPart))
    .join('/');
  const imageRequest = {
    bucket: s3BucketName,
    key,
    edits: imageEditRequest,

    // Note: editorJS resizing doesn't update version number
  };

  return `${cloudfrontHostname}/${base64js.fromByteArray(
    Uint8Array.from(new TextEncoder().encode(JSON.stringify(imageRequest)))
  )}`;
};

export const getLogoUrl = (originalImageUrl: string): string => {
  return getResizedUrl(originalImageUrl, {
    resize: {
      height: 48,
      fit: 'cover',
    },
  });
};

export const getThumbnailUrl = (originalImageUrl: string, width?: number): string => {
  return getResizedUrl(originalImageUrl, {
    resize: {
      width,
      fit: 'cover',
    },
  });
};

export const getDetailImageUrl = (originalImageUrl: string): string => {
  return getResizedUrl(originalImageUrl, {
    resize: {
      height: 800,
      fit: 'cover',
    },
  });
};

export const getBannerImageUrl = (originalImageUrl: string, width?: number): string => {
  return getResizedUrl(originalImageUrl, {
    resize: {
      height: 600,
      fit: 'cover',
      width,
    },
  });
};

export const getBackgroundBannerImageUrl = (
  originalImageUrl: string,
  screenWidth: 768 | 600 | 400 | null
): string => {
  let width = 2000;
  if (screenWidth) {
    width = screenWidth * 1.5;
  }

  return getResizedUrl(originalImageUrl, {
    resize: {
      fit: 'cover',
      width,
    },
  });
};

export const getOriginalUrlFromResizedUrl = (url: string) => {
  try {
    // Check if this is a Cloudfront URL
    if (!url.startsWith(cloudfrontHostname)) {
      return url;
    }

    // Extract the base64 part
    const base64Part = url.split(cloudfrontHostname + '/')[1];
    if (!base64Part) {
      return url;
    }

    // Decode the base64 string
    const jsonString = new TextDecoder().decode(base64js.toByteArray(base64Part));
    const imageRequest = JSON.parse(jsonString);

    // Reconstruct the original S3 URL
    return `https://${s3Hostname}/${imageRequest.key}`;
  } catch (e) {
    // If anything fails in the parsing, return the original URL
    return url;
  }
};
